import { Favorite, ProductListItem } from '@/types';
import { router } from '@inertiajs/react';
import { route } from 'ziggy-js';
import { create } from 'zustand';

export interface FavoriteState {
    // Данные избранного
    favorites: Favorite[];
    processingIds: number[];

    // Действия
    initializeFavorites: (favorites?: Favorite[]) => void;
    toggleFavorite: (product: ProductListItem) => void;
    isFavorite: (product_variation_id: number) => Favorite | undefined;
    isProcessing: (product_variation_id: number) => boolean;
}

export const useFavoriteStore = create<FavoriteState>((set, get) => ({
    // Начальное состояние
    favorites: [],
    processingIds: [],

    // Инициализация избранного данными с сервера
    initializeFavorites: (favorites?: Favorite[]) => {
        if (favorites) {
            set({ favorites });
        }
    },

    // Переключение состояния избранного
    toggleFavorite: (product) => {
        const { favorites, processingIds } = get();
        const favorite = favorites.find(
            (f) => f.product_variation_id === product.id,
        );

        // Добавляем ID в список обрабатываемых
        set({ processingIds: [...processingIds, product.id] });

        if (!favorite) {
            // Добавление
            router.post(
                route('favorite.add', { productVariation: product.id }),
                {},
                {
                    preserveScroll: true,
                    only: ['favorites', 'flash'],
                    onFinish: () => {
                        set((state) => ({
                            processingIds: state.processingIds.filter(
                                (id) => id !== product.id,
                            ),
                        }));
                    },
                },
            );
        } else {
            // Удаление
            router.delete(route('favorite.delete', { item: favorite.id }), {
                preserveScroll: true,
                only: ['favorites', 'flash'],
                onFinish: () => {
                    set((state) => ({
                        processingIds: state.processingIds.filter(
                            (id) => id !== product.id,
                        ),
                    }));
                },
            });
        }
    },

    // Проверка, находится ли товар в избранном
    isFavorite: (product_variation_id: number): Favorite | undefined => {
        const { favorites } = get();
        return favorites.find(
            (f) => f.product_variation_id === product_variation_id,
        );
    },

    // Проверка, обрабатывается ли сейчас запрос для этого товара
    isProcessing: (product_variation_id: number): boolean => {
        const { processingIds } = get();
        return processingIds.includes(product_variation_id);
    },
}));

// Хук для удобного использования store
export const useFavorites = () => {
    const store = useFavoriteStore();
    return {
        favorites: store.favorites,
        toggleFavorite: store.toggleFavorite,
        isFavorite: store.isFavorite,
        isProcessing: store.isProcessing,
        initializeFavorites: store.initializeFavorites,
    };
};

export default useFavoriteStore;
